import React, { useEffect, useState } from "react";
import { http } from "@/context/AuthContext";
import { Loader2, Plus, Trash2, Power, Mail, Clock, Eye, X } from "lucide-react";

const TRIGGERS = [
  { value: "customer.created", label: "New CDM customer (credit repair signup)" },
  { value: "invoice.paid", label: "Bookipi invoice paid" },
  { value: "invoice.overdue", label: "Bookipi invoice overdue" },
  { value: "newsletter.subscribe", label: "Newsletter signup" },
  { value: "contact.received", label: "Contact form submission" },
];

const ROLES = [
  { value: "public", label: "hello@thechhq.com" },
  { value: "credit", label: "credit@thechhq.com" },
  { value: "tax", label: "tax@thechhq.com" },
  { value: "billing", label: "billing@thechhq.com" },
  { value: "appointment", label: "appointment@thechhq.com" },
  { value: "intake", label: "intake@thechhq.com" },
];

export default function AdminDrips() {
  const [drips, setDrips] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [preview, setPreview] = useState(null);
  const [form, setForm] = useState({
    name: "", trigger_event: "customer.created", from_role: "credit",
    steps: [{ offset_days: 3, subject: "", body: "" }],
  });
  const [saving, setSaving] = useState(false);

  async function load() {
    const [d, s] = await Promise.all([
      http.get("/admin/drips"),
      http.get("/admin/drips/scheduled?limit=50"),
    ]);
    setDrips(d.data);
    setScheduled(s.data);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      await http.post("/admin/drips", form);
      setShowForm(false);
      setForm({ name: "", trigger_event: "customer.created", from_role: "credit", steps: [{ offset_days: 3, subject: "", body: "" }] });
      await load();
    } finally { setSaving(false); }
  }

  async function toggle(d) {
    await http.patch(`/admin/drips/${d.drip_id}?active=${!d.active}`);
    await load();
  }
  async function remove(d) {
    if (!window.confirm("Delete this drip and cancel its pending emails?")) return;
    await http.delete(`/admin/drips/${d.drip_id}`);
    await load();
  }
  async function openPreview(drip, stepIdx) {
    const { data } = await http.get(`/admin/drips/${drip.drip_id}/preview?step=${stepIdx}`);
    setPreview({ ...data, drip_name: drip.name, step_label: `Step ${stepIdx + 1} of ${drip.steps.length}` });
  }

  function updateStep(i, k, v) {
    const steps = [...form.steps];
    steps[i] = { ...steps[i], [k]: k === "offset_days" ? parseInt(v || 0, 10) : v };
    setForm({ ...form, steps });
  }

  return (
    <div className="space-y-8" data-testid="admin-drips">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow"><Mail className="inline w-3 h-3 -mt-0.5 mr-1" /> Email Automation</div>
          <h1 className="mt-2 text-4xl">Drip Sequences</h1>
          <p className="mt-2 text-navy/65">Schedule automated emails triggered by client events.</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} data-testid="new-drip-btn" className="btn-primary">
          <Plus className="w-4 h-4" /> New Drip
        </button>
      </header>

      {showForm && (
        <div className="card p-6 space-y-4" data-testid="drip-form">
          <input
            data-testid="drip-name"
            type="text" placeholder="Drip name (e.g. Credit Repair 30-day Nurture)"
            value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
            className="input-field"
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <select className="input-field" value={form.trigger_event} onChange={(e) => setForm({...form, trigger_event: e.target.value})} data-testid="drip-trigger">
              {TRIGGERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select className="input-field" value={form.from_role} onChange={(e) => setForm({...form, from_role: e.target.value})} data-testid="drip-from-role">
              {ROLES.map(r => <option key={r.value} value={r.value}>Send from: {r.label}</option>)}
            </select>
          </div>

          <div className="space-y-3">
            <div className="text-xs uppercase tracking-widest text-navy/55">Steps</div>
            {form.steps.map((s, i) => (
              <div key={i} className="border border-navy/10 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-navy/55">Send</span>
                  <input type="number" min="0" value={s.offset_days} onChange={(e) => updateStep(i, "offset_days", e.target.value)} className="input-field !w-20" />
                  <span className="text-xs text-navy/55">days after trigger</span>
                </div>
                <input type="text" placeholder="Email subject" value={s.subject} onChange={(e) => updateStep(i, "subject", e.target.value)} className="input-field" />
                <textarea rows={4} placeholder="Email body — use {first_name} or {email} for personalization" value={s.body} onChange={(e) => updateStep(i, "body", e.target.value)} className="input-field resize-none" />
              </div>
            ))}
            <button
              onClick={() => setForm({...form, steps: [...form.steps, { offset_days: 7, subject: "", body: "" }]})}
              className="text-sm text-gold hover:underline"
              data-testid="add-step"
            >
              + Add another step
            </button>
          </div>

          <div className="flex gap-3">
            <button onClick={save} disabled={saving || !form.name} data-testid="save-drip" className="btn-primary">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Drip"}
            </button>
            <button onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <Loader2 className="w-4 h-4 animate-spin text-navy/60" /> :
      drips.length === 0 ? (
        <div className="card p-10 text-center">
          <Mail className="w-10 h-10 text-gold mx-auto" />
          <h3 className="mt-3 text-2xl">No drip sequences yet.</h3>
          <p className="mt-2 text-navy/65">Create your first nurture sequence to auto-engage clients.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {drips.map((d) => (
            <div key={d.drip_id} className="card p-5 flex items-center justify-between" data-testid={`drip-${d.drip_id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-xl">{d.name}</h3>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${d.active ? "bg-teal/15 text-teal" : "bg-navy/10 text-navy/55"}`}>
                    {d.active ? "Active" : "Paused"}
                  </span>
                </div>
                <div className="text-xs text-navy/55 mt-1">
                  Trigger: <strong>{d.trigger_event}</strong> · From: <strong>{d.from_role}</strong> · {d.steps?.length || 0} step{(d.steps?.length||0)!==1?"s":""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {(d.steps || []).map((_, i) => (
                  <button key={i} onClick={() => openPreview(d, i)} className="text-navy/60 hover:text-gold p-2" title={`Preview step ${i+1}`} data-testid={`preview-${d.drip_id}-${i}`}>
                    <Eye className="w-4 h-4" />
                  </button>
                ))}
                <button onClick={() => toggle(d)} className="text-navy/60 hover:text-gold p-2" title={d.active ? "Pause" : "Resume"}>
                  <Power className="w-4 h-4" />
                </button>
                <button onClick={() => remove(d)} className="text-navy/60 hover:text-red-500 p-2" title="Delete">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {scheduled.length > 0 && (
        <div className="card p-6" data-testid="drip-queue">
          <h4 className="text-xl mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-gold" /> Email Queue (next {scheduled.length})</h4>
          <ul className="divide-y divide-navy/5 text-sm">
            {scheduled.slice(0, 10).map((s) => (
              <li key={s.id} className="py-3 flex items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="text-navy">{s.subject}</div>
                  <div className="text-xs text-navy/55">→ {s.to} · {s.scheduled_at?.slice(0, 16).replace("T", " ")}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest ${s.status === "sent" ? "text-teal" : s.status === "failed" ? "text-red-500" : "text-gold"}`}>{s.status}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setPreview(null)} data-testid="preview-modal">
          <div className="bg-soft rounded-2xl shadow-cardHover max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-navy/10 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-gold">Email Preview · {preview.step_label}</div>
                <h3 className="font-serif text-xl text-navy mt-1">{preview.drip_name}</h3>
                <div className="text-xs text-navy/55 mt-1">From: {preview.from_role}@thechhq.com · Subject: <strong>{preview.subject}</strong></div>
              </div>
              <button onClick={() => setPreview(null)} className="text-navy/60 hover:text-navy p-2" data-testid="close-preview">
                <X className="w-5 h-5" />
              </button>
            </div>
            <iframe
              title="email-preview"
              srcDoc={preview.html}
              className="flex-1 w-full bg-white"
              style={{ minHeight: "60vh", border: 0 }}
              data-testid="preview-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
}
